import { createBuyerClient } from "@libs/ticketing-client";
import {
  CARD_REUSE_CONSENT,
  createWalletApi,
  hostTokens,
} from "@libs/checkout-core";
import type {
  Clock,
  EnginePort,
  EventsPort,
  FrameMessage,
  HostTokenRequest,
  IdentityPort,
  PaymentsPort,
} from "@libs/checkout-core";

/**
 * The checkout core's ports as the app provides them (LIS-975). The same
 * core the web checkout runs, with:
 *
 * - engine: the generated buyer client and wallet routes against the
 *   engine's public API, authorised by the session's client secret — plain
 *   `fetch`, which React Native has;
 * - identity: the app answers every token request itself, over tRPC
 *   (`identity.checkoutToken`), from the fan's sign-in. No broker: the app's
 *   checkout runs no identity connection of its own;
 * - payments: the card island (a WebView), which confirms every payment the
 *   engine prepares, whatever made the method;
 * - events: no frame to tell; the few messages the sheet acts on are handed
 *   to it in-process;
 * - clock: the JS timers.
 */

export function nativeEngine(input: {
  apiBaseUrl: string;
  clientSecret: string;
}): EnginePort {
  const client = createBuyerClient({
    baseUrl: input.apiBaseUrl,
    clientSecret: input.clientSecret,
  });
  return {
    buyer: {
      get: (id) => client.get(id),
      update: (id, body) => client.update(id, body),
      complete: (id) => client.complete(id),
      finalize: (id) => client.finalize(id),
    },
    wallet: createWalletApi({
      baseUrl: input.apiBaseUrl,
      clientSecret: input.clientSecret,
    }),
  };
}

/** Asks the API for a checkout token for this session, as the signed-in fan.
 *  Card reuse is the one consent the API grants. */
export type AskToken = (request: {
  checkoutSessionSecret: string;
  consents: (typeof CARD_REUSE_CONSENT)[];
  reverify: boolean;
}) => Promise<{ token: string }>;

/** Identity: the app is the host, and answers every request itself. */
export function appIdentity(
  clientSecret: string,
  ask: AskToken,
): IdentityPort {
  return hostTokens(
    async ({ consents, reverify }: HostTokenRequest) =>
      (
        await ask({
          checkoutSessionSecret: clientSecret,
          // The core asks only for card reuse; anything else is not the
          // API's to grant.
          consents: consents.filter(
            (consent): consent is typeof CARD_REUSE_CONSENT =>
              consent === CARD_REUSE_CONSENT,
          ),
          reverify,
        })
      ).token,
  );
}

/** The island's confirmation, reached late: the WebView mounts after the
 *  core is made, and may be remounted. With no island there is nothing to
 *  confirm on, and the core shows the refusal. */
export function islandPayments(
  island: () => PaymentsPort | null,
): PaymentsPort {
  return {
    confirmPayment: (input) => {
      const port = island();
      return port
        ? port.confirmPayment(input)
        : Promise.reject(new Error("island_unavailable"));
    },
  };
}

/** What the sheet hears from the checkout: everything else the core says
 *  is for a page around a frame, and there is none. */
export type CheckoutEvents = {
  /** The hold and when it ends. */
  onHeld?: (expiresAt: string) => void;
  /** Money is moving: the hold is no longer the sheet's to give back. */
  onPaying?: () => void;
  /** Paid, and the tickets issued. */
  onPaid?: (orderId: string | null) => void;
  /** "You're going" has been seen: the success screen takes over. */
  onSucceeded?: (result: { method: string | null; email: string | null }) => void;
  /** The hold ran out; the tickets are back on sale. */
  onExpired?: () => void;
  /** The × on the destination: back to the tickets. */
  onChangeContact?: () => void;
};

/** Handlers that can change after the core is made: the core holds
 *  `events`, the sheet `set`s its latest handlers each render. */
export function eventRelay() {
  let current: CheckoutEvents = {};
  const events: Required<CheckoutEvents> = {
    onHeld: (expiresAt) => current.onHeld?.(expiresAt),
    onPaying: () => current.onPaying?.(),
    onPaid: (orderId) => current.onPaid?.(orderId),
    onSucceeded: (result) => current.onSucceeded?.(result),
    onExpired: () => current.onExpired?.(),
    onChangeContact: () => current.onChangeContact?.(),
  };
  return {
    events,
    set: (next: CheckoutEvents) => {
      current = next;
    },
  };
}

export function nativeEvents(on: CheckoutEvents): EventsPort {
  return {
    emit: (message: FrameMessage) => {
      switch (message.type) {
        case "session":
          on.onHeld?.(message.expires_at);
          return;
        case "paying":
          on.onPaying?.();
          return;
        case "paid":
          on.onPaid?.(message.order_id);
          return;
        case "succeeded":
          on.onSucceeded?.({
            method: message.method_label,
            email: message.buyer_email,
          });
          return;
        case "expired":
          on.onExpired?.();
          return;
        case "change_contact":
          on.onChangeContact?.();
          return;
        default:
          return;
      }
    },
    // The success screen follows in the same sheet: nothing to hand over.
    statusOrigin: () => null,
  };
}

export const nativeClock: Clock = {
  now: () => Date.now(),
  setTimeout: (run, ms) => setTimeout(run, ms),
  clearTimeout: (handle) =>
    clearTimeout(handle as ReturnType<typeof setTimeout>),
};
